import { createPortal } from 'react-dom'
import { useModal } from './use-modal'
import { Button, CloseLgIcon } from '@/components/atoms'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  keepMounted?: boolean
  hideCloseButton?: boolean
  className?: string
  chilrenClassName?: string
}

export function Modal({
  isOpen,
  onClose,
  children,
  keepMounted,
  hideCloseButton,
  className = '',
  chilrenClassName = 'max-h-[calc(100dvh-4rem)] md:max-h-[calc(100dvh-6rem)]',
}: ModalProps) {
  const { mounted, visible, hidden } = useModal(isOpen, onClose, keepMounted)

  if (!mounted) return <></>

  return createPortal(
    <div
      style={
        hidden
          ? {
              position: 'fixed',
              visibility: 'hidden',
              overflow: 'hidden',
              width: 0,
              height: 0,
            }
          : undefined
      }
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-(--z-modal) bg-(--overlay-bg) backdrop-blur-sm transition-opacity duration-300 md:pt-18 ${visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="modal-backdrop"
      />
      {/* Panel container */}
      <div
        className={`fixed inset-0 z-(--z-modal) flex items-end justify-center md:items-center ${!visible ? 'pointer-events-none' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        {/* Panel */}
        <div
          className={`relative w-full overflow-hidden rounded-t-3xl bg-white transition md:max-w-2xl md:rounded-3xl ${
            visible
              ? 'translate-y-0 opacity-100 md:scale-100'
              : 'translate-y-full opacity-100 md:translate-y-0 md:scale-95 md:opacity-0'
          } ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideCloseButton && (
            <Button
              onClick={onClose}
              variant="link"
              className="absolute top-4 right-4 z-(--z-header) flex items-center justify-center rounded-full p-2 text-(--color-indigo-base)"
              aria-label="Close"
              data-testid="modal-close-button"
            >
              <CloseLgIcon className="h-6 w-6" />
            </Button>
          )}
          <div
            className={`overflow-y-auto overscroll-contain ${chilrenClassName}`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
